import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatDate, formatTime } from "@/lib/utils";
import { useCustomerNotes, useAddCustomerNote } from "@/hooks/useCustomerNotes";
import { Loader2, Plus, StickyNote } from "lucide-react";
import { mockUsers } from "@/lib/mock-users";

interface NotesTabProps {
  customerId: string;
  customerName: string;
}

export function NotesTab({ customerId, customerName }: NotesTabProps) {
  const [noteContent, setNoteContent] = useState("");
  const { data: notes = [], isLoading } = useCustomerNotes(customerId);
  const addNoteMutation = useAddCustomerNote();

  // For demo purposes, use the first user as the current user
  // In a real app, this would come from an authentication context
  const currentUser = mockUsers[0];

  const handleAddNote = async () => {
    if (!noteContent.trim()) {
      toast.error("Please enter a note");
      return;
    }

    try {
      await addNoteMutation.mutateAsync({
        customerId,
        content: noteContent.trim(),
        userId: currentUser.id,
        userName: `${currentUser.firstName} ${currentUser.lastName}`,
      });

      setNoteContent("");
      toast.success("Note added successfully");
    } catch (error) {
      toast.error("Failed to add note");
      console.error("Error adding note:", error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Allow Ctrl+Enter or Cmd+Enter to submit
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleAddNote();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading notes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add Note Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add New Note
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Enter your note here... (Ctrl+Enter to save)"
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-[100px] resize-none"
            disabled={addNoteMutation.isPending}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Note will be saved as {currentUser.firstName} {currentUser.lastName}
            </p>
            <Button
              onClick={handleAddNote}
              disabled={!noteContent.trim() || addNoteMutation.isPending}
            >
              {addNoteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Note
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notes History Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StickyNote className="h-4 w-4" />
              Notes History
            </div>
            <Badge variant="secondary">{notes.length} {notes.length === 1 ? "note" : "notes"}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <StickyNote className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-1">No notes yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Add your first note to start tracking important information about {customerName}.
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-4">
                {notes.map((note, index) => (
                  <div key={note.id}>
                    <div className="space-y-2">
                      {/* Note Header */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">
                              {note.createdBy.name}
                            </span>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(note.createdAt)}
                            </span>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-muted-foreground">
                              {formatTime(note.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Note Content */}
                      <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {note.content}
                      </div>
                    </div>

                    {/* Separator between notes */}
                    {index < notes.length - 1 && (
                      <Separator className="my-4" />
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
